// Library methods
import { useTranslation } from "react-i18next";

// MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from "@mui/lab/TimelineOppositeContent";
import DefaultModal from "../../../../components/UI/DefaultModal";

// Styles
import { ModalCustomCancel } from "../../../../styles/muiStylesHelper";

// Utils
import { getLocalDateTime } from "../../../../utils/dateHelper";
import { ADMIN_TIMEZONE } from "../../../../enums";

const DeviceHistoryModal = ({ open, deviceHistory, onClose, onCancel }) => {
  // internationalization
  const { t } = useTranslation();

  const sortedDeviceHistory = deviceHistory.sort((a, b) =>
    b.assignmentDate.localeCompare(a.assignmentDate)
  );

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("device_history_modal_title")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button variant="text" sx={ModalCustomCancel} onClick={onCancel}>
            {t("button_cancel")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        {sortedDeviceHistory.length === 0 && (
          <Typography
            noWrap
            variant="body1"
            sx={{ textTransform: "capitalize" }}
            color="text.secondary"
            textAlign="center"
          >
            {t("device_history_modal_empty")}
          </Typography>
        )}
        {sortedDeviceHistory.length > 0 && (
          <Timeline
            sx={{
              [`& .${timelineOppositeContentClasses.root}`]: {
                flex: 0.75,
              },
              margin: 0,
            }}
          >
            {sortedDeviceHistory.map((device, index) => {
              const displayConnector = index !== deviceHistory.length - 1;
              return (
                <TimelineItem key={device.id}>
                  <TimelineOppositeContent align="right" variant="body2">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {device?.clinic?.name}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot />
                    {displayConnector && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    {device?.unassignmentDate && (
                      <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {t("device_history_modal_unassignment_date")}
                        </Typography>
                        <Typography variant="body2" ml={{ xs: 0, sm: 1 }}>
                          {getLocalDateTime(
                            device?.unassignmentDate,
                            ADMIN_TIMEZONE
                          )}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {t("device_history_modal_assignment_date")}
                      </Typography>
                      <Typography variant="body2" ml={{ xs: 0, sm: 1 }}>
                        {getLocalDateTime(
                          device?.assignmentDate,
                          ADMIN_TIMEZONE
                        )}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default DeviceHistoryModal;
